import React from "react";
import { ExpandableHeader } from "../../../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { DisplayDetail } from "../../../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import { filterChargesForRole, FSC_CODE } from "../../../../../../utils/chargeUtils";

const toCents = (v) => Math.round(Number(v ?? 0) * 100);
const fromCents = (c) => c / 100;

const fmt = (value, currency) => {
  if (value === null || value === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${Number(value).toFixed(2)}`;
  }
};

const legNum = (leg) => {
  const m = String(leg?.shipmentNumber || "").match(/-(\d+)$/);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
};

const legStopLabel = (stop) => stop?.stopName || [stop?.city, stop?.state].filter(Boolean).join(", ") || "—";

const legCarrier = (leg) => {
  if (leg.carrierOfficeCode?.name) return leg.carrierOfficeCode.name;
  const p = (leg.shipmentParties || []).find(
    (p) => (p.type || "").toUpperCase() === "CARRIER"
  );
  return p?.name || "—";
};

const legCostCents = (leg) =>
  (leg.charges || [])
    .filter((c) => ["COST", "COST_PER_STOP"].includes((c.type || "").toUpperCase()))
    .reduce((sum, c) => sum + toCents(c.amount), 0);

const legApprovalStatus = (leg) =>
  (leg.charges || []).every(
    (c) => (c.chargeApprovalStatus || "PREAPPROVED") === "APPROVED"
  )
    ? "APPROVED"
    : "PREAPPROVED";

const StatusDot = ({ status, light }) => {
  const dotClass =
    status === "APPROVED"
      ? light ? "bg-emerald-400" : "bg-emerald-500"
      : light ? "bg-amber-400" : "bg-amber-400";
  const textClass =
    status === "APPROVED"
      ? light ? "text-white/70" : "text-emerald-700"
      : light ? "text-white/70" : "text-amber-700";
  return (
    <span className={`inline-flex items-center gap-1 text-dense-xs font-medium ${textClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
      {status === "APPROVED" ? "Approved" : "Pre-approved"}
    </span>
  );
};

/* Read-only value box — matches the existing charge display in ShipmentDetailsView */
const ValueBox = ({ label, value }) => (
  <div className="flex flex-col min-w-[clamp(8rem,12vw,10rem)]">
    <span className="text-dense-xs text-gray-500">{label}</span>
    <div className="relative flex items-center w-full px-fluid-2 py-fluid-1.5 mt-fluid-0.25 rounded-md border text-fluid-sm font-inter bg-[#FAFAFB] text-gray-700 border-gray-300">
      <span>{value}</span>
    </div>
  </div>
);

const ChildLegChargesAccordion = ({ legs, currency, isShipper }) => {
  if (!legs || legs.length === 0) return null;

  const sorted = [...legs].sort((a, b) => legNum(a) - legNum(b));
  const grandTotalCents = sorted.reduce((sum, leg) => sum + legCostCents(leg), 0);
  const role = isShipper ? "shipper" : "carrier";

  return (
    <div>
      <div className="px-fluid-3 pt-fluid-3">
      {sorted.map((leg, idx) => {
        const stops = leg.shipmentStops || [];
        const origin = legStopLabel(
          stops.find((s) => ["PICKUP", "ORIGIN"].includes(s.stopType)) || stops[0]
        );
        const dest = legStopLabel(
          stops.find((s) =>
            ["DELIVERY", "DESTINATION", "DROP_OFF"].includes(s.stopType)
          ) || stops[stops.length - 1]
        );
        const carrier = legCarrier(leg);
        const totalCents = legCostCents(leg);
        const status = legApprovalStatus(leg);
        const charges = filterChargesForRole(leg.charges || [], role);
        const n = String(legNum(leg) === Number.MAX_SAFE_INTEGER ? idx + 1 : legNum(leg));

        return (
          <ExpandableHeader
            key={leg.shipmentNumber || idx}
            headerType="secondary"
            initiallyExpanded={false}
            headerSection={
              <div className="flex items-center justify-between w-full gap-3 min-w-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-dense-xs font-bold text-white/40 uppercase tracking-widest shrink-0">
                    Leg {n}
                  </span>
                  <span className="text-fluid-sm font-medium text-white truncate">
                    {origin}
                    <span className="mx-1.5 text-white/40">›</span>
                    {dest}
                  </span>
                  <span className="hidden sm:block text-fluid-sm text-white/50 truncate">
                    {carrier}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-fluid-sm font-semibold tabular-nums text-white">
                    {fmt(fromCents(totalCents), leg.currency || currency)}
                  </span>
                </div>
              </div>
            }
          >
            {/* Charge rows — same grid format as the single-leg charge details view */}
            <div className="bg-[#F9FAFE] p-fluid-3 space-y-fluid-3">
              {charges.length === 0 ? (
                <p className="text-fluid-sm text-gray-400 text-center py-fluid-2">
                  No charges recorded for this leg.
                </p>
              ) : (
                charges.map((charge, ci) => {
                  const chargeLabel = [
                    charge.code,
                    charge.chargeName && charge.chargeName !== charge.code
                      ? charge.chargeName
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" - ") || "—";

                  const approvalStatus =
                    charge.chargeApprovalStatus === "APPROVED"
                      ? "APPROVED"
                      : "PRE-APPROVED";

                  return (
                    <div
                      key={ci}
                      className="grid grid-cols-6 gap-x-fluid-2 gap-y-fluid-3 text-gray-800"
                    >
                      <DisplayDetail label="Charge" value={chargeLabel} />
                      <ValueBox
                        label="Applied Rate"
                        value={
                          charge.appliedRate != null
                            ? fmt(charge.appliedRate, charge.currency || leg.currency || currency)
                            : "—"
                        }
                      />
                      <DisplayDetail
                        label="Rate Basis"
                        value={charge.rateBasis || "FLAT"}
                      />
                      <DisplayDetail
                        label="Quantity"
                        value={charge.quantity != null ? charge.quantity : "—"}
                      />
                      <ValueBox
                        label="Total Amount"
                        value={fmt(charge.amount, charge.currency || leg.currency || currency)}
                      />
                      <DisplayDetail
                        label="Approval Status"
                        value={approvalStatus}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </ExpandableHeader>
        );
      })}

      </div>
      {/* Grand total — carrier view only; shipper sees totals in ParentChargeSummary above */}
      {!isShipper && (
        <div className="flex items-center justify-between px-fluid-3 py-fluid-2 border-t-2 border-gray-200 bg-[#F9FAFE]">
          <span className="text-dense-xs font-semibold text-gray-500 uppercase tracking-wider">
            Grand Total
          </span>
          <span className="text-fluid-base font-bold tabular-nums text-[#223688]">
            {fmt(fromCents(grandTotalCents), currency)}
          </span>
        </div>
      )}
    </div>
  );
};

export default ChildLegChargesAccordion;
